// ============================================
// FAMUZA MARKET — Product Detail Page
// With: Add to Cart popup, Continue Shopping, Buy Now
// ============================================

import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';

const WaIcon = ({ size = 20, color = 'white' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ flexShrink: 0 }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.118 1.528 5.845L0 24l6.335-1.508A11.953 11.953 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.006-1.366l-.36-.213-3.724.977.994-3.634-.234-.374A9.818 9.818 0 1112 21.818z"/>
  </svg>
);

const CartIcon = ({ size = 22, color = 'rgba(245,240,232,0.9)' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1"/>
    <circle cx="20" cy="21" r="1"/>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
  </svg>
);

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

export default function Product() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { whatsAppEnquiry, callSeller, formatPrice, addToCart, cart, showNotification } = useApp();
  const isMobile = useIsMobile();

  const [product, setProduct] = useState(null);
  const [images, setImages] = useState([]);
  const [activeImage, setActiveImage] = useState(0);
  const [related, setRelated] = useState([]);
  const [activeTab, setActiveTab] = useState('description');
  const [loading, setLoading] = useState(true);
  const [showCartPopup, setShowCartPopup] = useState(false);

  const cartCount = cart ? cart.reduce((sum, item) => sum + (item.qty || 1), 0) : 0;

  useEffect(() => {
    async function fetchProduct() {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();
      if (error) { setLoading(false); return; }
      setProduct(data);

      // Fetch additional images
      const { data: imgData } = await supabase
        .from('product_images')
        .select('*')
        .eq('product_id', id)
        .order('position', { ascending: true });

      // Build full image list: main image first, then extras
      const allImages = [
        data.image_url,
        ...((imgData || []).map(i => i.image_url).filter(url => url !== data.image_url))
      ].filter(Boolean);
      setImages(allImages);
      setActiveImage(0);

      const { data: rel } = await supabase
        .from('products')
        .select('*')
        .eq('category', data.category)
        .neq('id', id)
        .limit(4);
      setRelated(rel || []);
      setLoading(false);
    }
    fetchProduct();
  }, [id]);

  if (loading) {
    return (
      <div style={{ background: '#0D0D0D', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F5F0E8' }}>
        Loading...
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ background: '#0D0D0D', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem', color: '#F5F0E8' }}>
        <div style={{ fontSize: '3rem' }}>&#128269;</div>
        <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.25rem', fontWeight: 800 }}>Item not found</div>
        <div style={{ fontSize: '0.85rem', color: 'rgba(245,240,232,0.5)' }}>This listing may have been sold or removed</div>
        <button onClick={() => navigate('/listings')} style={{ background: '#E8622A', color: '#fff', border: 'none', borderRadius: 100, padding: '0.7rem 1.5rem', fontSize: '0.88rem', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer', marginTop: '0.5rem' }}>
          Browse other listings
        </button>
      </div>
    );
  }

  const discount = product.old_price
    ? Math.round(((product.old_price - product.price) / product.old_price) * 100)
    : null;

  const handleWhatsApp = () => {
    const msg = `Hi! I saw your listing on FAMUZA Market and I am interested in:\n\n*${product.title}*\nPrice: K${product.price.toLocaleString()}\nCondition: ${product.condition}\n\nIs it still available?`;
    window.open(`https://wa.me/${product.seller_phone || '260970000000'}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleBuyNow = () => {
    const msg = `Hi! I want to BUY this item from FAMUZA Market:\n\n*${product.title}*\nPrice: K${product.price.toLocaleString()}\nCondition: ${product.condition}\n\nPlease let me know how to arrange payment and delivery. Thank you!`;
    window.open(`https://wa.me/${product.seller_phone || '260970000000'}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const text = `Check out this listing on FAMUZA Market: ${product.title} for K${product.price.toLocaleString()}`;
    if (platform === 'whatsapp') window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
    if (platform === 'facebook') window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
    if (platform === 'copy') { navigator.clipboard.writeText(url); showNotification('Link copied!', 'success'); }
  };

  const handleAddToCart = () => {
    addToCart({
      id: product.id,
      name: product.title,
      price: product.price,
      sellerId: product.seller_id,
      sellerName: product.seller,
      sellerPhone: product.seller_phone,
      image: product.image_url,
      qty: 1
    });
    setShowCartPopup(true);
  };

  return (
    <div style={{ background: '#0D0D0D', minHeight: '100vh' }}>

      {/* Add to Cart Popup */}
      {showCartPopup && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
          onClick={() => setShowCartPopup(false)}>
          {/* Backdrop */}
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} />
          {/* Popup card */}
          <div onClick={e => e.stopPropagation()}
            style={{ position: 'relative', background: '#1A1A1A', borderRadius: '20px 20px 0 0', padding: '1.5rem', width: '100%', maxWidth: 500, border: '0.5px solid rgba(255,255,255,0.1)', animation: 'slideUp 0.25s ease' }}>
            <style>{`
              @keyframes slideUp {
                from { transform: translateY(100%); }
                to { transform: translateY(0); }
              }
            `}</style>

            {/* Item added header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
              <div style={{ width: 64, height: 64, borderRadius: 12, overflow: 'hidden', background: '#0D0D0D', flexShrink: 0, border: '2px solid rgba(37,211,102,0.4)' }}>
                <img src={product.image_url} alt={product.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#25D366', fontFamily: 'DM Sans, sans-serif' }}>Added to basket!</span>
                </div>
                <div style={{ fontSize: '0.82rem', color: '#F5F0E8', fontWeight: 500, marginBottom: 2, lineHeight: 1.3 }}>{product.title}</div>
                <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.95rem', fontWeight: 800, color: '#8B6914' }}>{formatPrice(product.price)}</div>
              </div>
            </div>

            {/* Cart summary */}
            <div style={{ background: '#0D0D0D', borderRadius: 10, padding: '0.75rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CartIcon size={18} color="rgba(245,240,232,0.6)" />
                <span style={{ fontSize: '0.82rem', color: 'rgba(245,240,232,0.6)', fontFamily: 'DM Sans, sans-serif' }}>
                  {cartCount} item{cartCount !== 1 ? 's' : ''} in basket
                </span>
              </div>
              <span style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.88rem', fontWeight: 700, color: '#F5F0E8' }}>
                {formatPrice(cart ? cart.reduce((sum, item) => sum + (item.price * (item.qty || 1)), 0) : 0)}
              </span>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => { setShowCartPopup(false); }}
                style={{ flex: 1, padding: '0.875rem', background: 'transparent', color: '#F5F0E8', border: '0.5px solid rgba(255,255,255,0.2)', borderRadius: 12, fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, cursor: 'pointer' }}
              >
                Continue Shopping
              </button>
              <button
                onClick={() => { setShowCartPopup(false); navigate('/cart'); }}
                style={{ flex: 1, padding: '0.875rem', background: '#E8622A', color: '#fff', border: 'none', borderRadius: 12, fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 700, cursor: 'pointer' }}
              >
                View Basket
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top nav bar */}
      <div style={{ background: '#161616', borderBottom: '0.5px solid rgba(255,255,255,0.07)', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.1rem', fontWeight: 800, color: '#F5F0E8' }}>
            FAMUZA <span style={{ color: '#E8622A' }}>Market</span>
          </div>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Link to="/listings" style={{ fontSize: '0.78rem', color: 'rgba(245,240,232,0.5)', textDecoration: 'none' }}>
            Browse
          </Link>
          <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => navigate('/cart')}>
            <CartIcon size={22} />
            {cartCount > 0 && (
              <div style={{ position: 'absolute', top: -7, right: -7, background: '#E8622A', color: '#fff', borderRadius: '50%', width: 18, height: 18, fontSize: '0.62rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {cartCount}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0.75rem 1rem 0', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.72rem', color: 'rgba(245,240,232,0.4)', flexWrap: 'wrap' }}>
        <Link to="/" style={{ color: 'rgba(245,240,232,0.4)', textDecoration: 'none' }}>Home</Link>
        <span>/</span>
        <Link to="/listings" style={{ color: 'rgba(245,240,232,0.4)', textDecoration: 'none' }}>Listings</Link>
        <span>/</span>
        <Link to={`/listings?cat=${encodeURIComponent(product.category)}`} style={{ color: 'rgba(245,240,232,0.4)', textDecoration: 'none' }}>{product.category}</Link>
        <span>/</span>
        <span style={{ color: '#F5F0E8' }}>{product.title}</span>
      </div>

      {/* Main layout */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '1rem', display: isMobile ? 'block' : 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', alignItems: 'start' }}>

        {/* LEFT — Image Gallery */}
        <div>
          {/* Main image */}
          <div style={{ borderRadius: 18, overflow: 'hidden', background: '#1E1E1E', position: 'relative', marginBottom: '0.75rem' }}>
            <img
              src={images[activeImage] || product.image_url}
              alt={product.title}
              style={{ width: '100%', height: isMobile ? 300 : 420, objectFit: 'cover', display: 'block', transition: 'opacity 0.2s' }}
              onError={e => { e.target.style.display = 'none'; }}
            />
            {discount && (
              <div style={{ position: 'absolute', top: 14, left: 14, background: '#E8622A', color: '#fff', fontSize: '0.85rem', fontWeight: 700, padding: '5px 12px', borderRadius: 100 }}>
                -{discount}% OFF
              </div>
            )}
            {/* Image counter */}
            {images.length > 1 && (
              <div style={{ position: 'absolute', bottom: 14, left: 14, background: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: '0.72rem', fontWeight: 600, padding: '3px 10px', borderRadius: 100 }}>
                {activeImage + 1} / {images.length}
              </div>
            )}
            {/* Arrow navigation */}
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setActiveImage(i => (i - 1 + images.length) % images.length)}
                  style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 34, height: 34, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  ‹
                </button>
                <button
                  onClick={() => setActiveImage(i => (i + 1) % images.length)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 34, height: 34, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  ›
                </button>
              </>
            )}
            <div style={{ position: 'absolute', bottom: 14, right: 14, display: 'flex', gap: '0.5rem' }}>
              {[['whatsapp', '💬'], ['facebook', '📘'], ['copy', '🔗']].map(([p, icon]) => (
                <button key={p} onClick={() => handleShare(p)} style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(13,13,13,0.75)', border: '0.5px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 15 }}>
                  {icon}
                </button>
              ))}
            </div>
          </div>

          {/* Thumbnails — only show if multiple images */}
          {images.length > 1 && (
            <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.25rem', scrollbarWidth: 'none', marginBottom: '0.875rem' }}>
              {images.map((img, i) => (
                <div
                  key={i}
                  onClick={() => setActiveImage(i)}
                  style={{ width: 64, height: 64, flexShrink: 0, borderRadius: 10, overflow: 'hidden', cursor: 'pointer', border: `2px solid ${activeImage === i ? '#E8622A' : 'transparent'}`, opacity: activeImage === i ? 1 : 0.6, transition: 'all 0.15s', background: '#1E1E1E' }}>
                  <img src={img} alt={`View ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <div style={{ background: 'rgba(37,211,102,0.1)', border: '0.5px solid rgba(37,211,102,0.25)', borderRadius: 100, padding: '4px 12px', fontSize: '0.75rem', color: '#25D366', fontWeight: 500 }}>✓ {product.condition}</div>
            <div style={{ background: 'rgba(232,98,42,0.1)', border: '0.5px solid rgba(232,98,42,0.25)', borderRadius: 100, padding: '4px 12px', fontSize: '0.75rem', color: '#E8622A', fontWeight: 500 }}>{product.category}</div>
            <div style={{ background: 'rgba(232,98,42,0.08)', border: '0.5px solid rgba(232,98,42,0.2)', borderRadius: 100, padding: '4px 12px', fontSize: '0.75rem', color: '#E8622A', fontWeight: 500 }}>🚚 Delivery available</div>
            <div style={{ background: 'rgba(74,144,217,0.08)', border: '0.5px solid rgba(74,144,217,0.2)', borderRadius: 100, padding: '4px 12px', fontSize: '0.75rem', color: '#4A90D9', fontWeight: 500 }}>💬 Negotiable</div>
          </div>
        </div>

        {/* RIGHT — Details */}
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: isMobile ? '1.3rem' : '1.6rem', fontWeight: 800, color: '#F5F0E8', lineHeight: 1.2, letterSpacing: '-0.02em', marginBottom: '1rem' }}>
            {product.title}
          </h1>

          {/* Price */}
          <div style={{ background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: '1.25rem', marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.875rem' }}>
              <span style={{ fontFamily: 'Syne, sans-serif', fontSize: '2rem', fontWeight: 800, color: '#8B6914' }}>{formatPrice(product.price)}</span>
              {product.old_price && (
                <>
                  <span style={{ fontSize: '1rem', color: 'rgba(245,240,232,0.35)', textDecoration: 'line-through' }}>{formatPrice(product.old_price)}</span>
                  <span style={{ background: 'rgba(232,98,42,0.12)', border: '0.5px solid rgba(232,98,42,0.3)', color: '#E8622A', fontSize: '0.78rem', fontWeight: 600, padding: '3px 10px', borderRadius: 100 }}>
                    Save {formatPrice(product.old_price - product.price)}
                  </span>
                </>
              )}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'rgba(245,240,232,0.45)', marginBottom: '0.6rem' }}>Pay via:</div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {['📱 MTN MoMo', '📡 Airtel Money', '💵 Cash on Delivery'].map(m => (
                <span key={m} style={{ background: '#1E1E1E', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 8, padding: '4px 10px', fontSize: '0.75rem', color: 'rgba(245,240,232,0.6)' }}>{m}</span>
              ))}
            </div>
          </div>

          {/* CTA Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', marginBottom: '1.25rem' }}>

            {/* Buy Now — most prominent */}
            <button onClick={handleBuyNow}
              style={{ width: '100%', padding: '1rem', background: '#E8622A', color: '#fff', border: 'none', borderRadius: 12, fontSize: '1rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', boxShadow: '0 4px 20px rgba(232,98,42,0.35)' }}>
              <WaIcon size={18} color="white" /> Buy Now
            </button>

            {/* WhatsApp + Call */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.6rem' }}>
              <button onClick={handleWhatsApp}
                style={{ padding: '0.875rem', background: '#25D366', color: '#fff', border: 'none', borderRadius: 12, fontSize: '0.95rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem' }}>
                <WaIcon size={18} /> Chat on WhatsApp
              </button>
              <button onClick={() => callSeller(product.seller_phone)} title="Call seller"
                style={{ padding: '0.875rem 1.1rem', background: 'rgba(74,144,217,0.12)', border: '0.5px solid rgba(74,144,217,0.3)', borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', color: '#4A90D9', fontSize: '0.82rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 500, whiteSpace: 'nowrap' }}>
                📞 Call
              </button>
            </div>

            {/* Add to basket */}
            <button onClick={handleAddToCart}
              style={{ width: '100%', padding: '0.875rem', background: 'transparent', color: 'rgba(245,240,232,0.7)', border: '0.5px solid rgba(255,255,255,0.2)', borderRadius: 12, fontSize: '0.95rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
              <CartIcon size={18} color="rgba(245,240,232,0.7)" /> Add to basket
            </button>
          </div>

          {/* Trust signals */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1.25rem' }}>
            {[['✓', 'Verified seller', '#25D366'], ['⚡', 'Fast responder', '#C49A2A'], ['🔒', 'Safe transaction', '#4A90D9'], ['📍', 'Lusaka pickup', '#E8622A']].map(([icon, text, color]) => (
              <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '0.6rem 0.75rem', fontSize: '0.78rem', color: 'rgba(245,240,232,0.6)' }}>
                <span style={{ color, fontSize: '0.9rem' }}>{icon}</span> {text}
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '0.5px solid rgba(255,255,255,0.07)', marginBottom: '1rem' }}>
            {[['description', 'Description'], ['details', 'Details'], ['delivery', 'Delivery']].map(([tab, label]) => (
              <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '0.65rem 1.1rem', background: 'transparent', border: 'none', borderBottom: `2px solid ${activeTab === tab ? '#E8622A' : 'transparent'}`, color: activeTab === tab ? '#E8622A' : 'rgba(245,240,232,0.5)', fontSize: '0.85rem', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer', marginBottom: -1 }}>
                {label}
              </button>
            ))}
          </div>

          <div style={{ fontSize: '0.85rem', color: 'rgba(245,240,232,0.6)', lineHeight: 1.75 }}>
            {activeTab === 'description' && <p>{product.description || 'Contact seller for more details.'}</p>}
            {activeTab === 'details' && (
              <div>
                {[['Category', product.category], ['Condition', product.condition], ['Seller', product.seller || 'FAMUZA Seller'], ['Location', product.location || 'Lusaka, Zambia'], ['Stock', product.stock || 1], ['Listing ID', `#FM-${String(product.id).slice(0, 8).toUpperCase()}`]].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.45rem 0', borderBottom: '0.5px solid rgba(255,255,255,0.05)', fontSize: '0.82rem' }}>
                    <span style={{ color: 'rgba(245,240,232,0.4)' }}>{k}</span>
                    <span style={{ color: '#F5F0E8', fontWeight: 500 }}>{v}</span>
                  </div>
                ))}
              </div>
            )}
            {activeTab === 'delivery' && (
              <div>
                {[['Pickup', 'Available — collect from seller in Lusaka'], ['Delivery', 'Negotiate with seller via WhatsApp'], ['Payment', 'MTN MoMo, Airtel Money, or Cash on Delivery'], ['Response time', 'Usually within a few hours']].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.45rem 0', borderBottom: '0.5px solid rgba(255,255,255,0.05)', fontSize: '0.82rem' }}>
                    <span style={{ color: 'rgba(245,240,232,0.4)' }}>{k}</span>
                    <span style={{ color: '#F5F0E8', fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{v}</span>
                  </div>
                ))}
                <div style={{ marginTop: '0.875rem', background: 'rgba(37,211,102,0.06)', border: '0.5px solid rgba(37,211,102,0.2)', borderRadius: 10, padding: '0.75rem', fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)' }}>
                  💬 Tap the WhatsApp button above to arrange delivery or pickup directly with the seller.
                </div>
              </div>
            )}
          </div>

          {/* Seller card */}
          <div style={{ background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: '1.1rem', marginTop: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(232,98,42,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.1rem', color: '#E8622A', flexShrink: 0 }}>
                {product.seller ? product.seller[0] : 'F'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.92rem', fontWeight: 500, color: '#F5F0E8', marginBottom: 3 }}>{product.seller || 'FAMUZA Seller'}</div>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '0.65rem', color: '#25D366', background: 'rgba(37,211,102,0.1)', border: '0.5px solid rgba(37,211,102,0.2)', padding: '2px 8px', borderRadius: 100 }}>✓ Verified Seller</div>
                  <div style={{ fontSize: '0.65rem', color: '#C49A2A', background: 'rgba(196,154,42,0.1)', border: '0.5px solid rgba(196,154,42,0.2)', padding: '2px 8px', borderRadius: 100 }}>⭐ UNIFAM Partner</div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.75rem' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#25D366', display: 'inline-block', boxShadow: '0 0 6px rgba(37,211,102,0.7)' }} />
              <span style={{ fontSize: '0.75rem', color: '#25D366', fontWeight: 500 }}>Online now</span>
              <span style={{ fontSize: '0.68rem', color: 'rgba(245,240,232,0.3)', marginLeft: '0.25rem' }}>· Usually replies in under 1 hour</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <button onClick={handleWhatsApp} style={{ padding: '0.65rem', background: '#25D366', color: '#fff', border: 'none', borderRadius: 10, fontSize: '0.82rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                <WaIcon size={15} /> WhatsApp
              </button>
              <Link to={`/listings?cat=${encodeURIComponent(product.category)}`} style={{ textDecoration: 'none' }}>
                <button style={{ width: '100%', padding: '0.65rem', background: 'transparent', border: '0.5px solid rgba(255,255,255,0.1)', color: 'rgba(245,240,232,0.7)', borderRadius: 10, fontSize: '0.82rem', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer' }}>
                  More like this
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Related products */}
      {related.length > 0 && (
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 1.5rem 3rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.2rem', fontWeight: 700, color: '#F5F0E8' }}>
              You may also <span style={{ color: '#E8622A' }}>like</span>
            </h2>
            <Link to={`/listings?cat=${encodeURIComponent(product.category)}`} style={{ fontSize: '0.82rem', color: '#E8622A', textDecoration: 'none' }}>See all →</Link>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.875rem' }}>
            {related.map(item => (
              <div key={item.id} onClick={() => { navigate(`/product/${item.id}`); window.scrollTo(0, 0); }}
                style={{ background: '#F5EFE0', borderRadius: 12, overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.2s', border: '0.5px solid rgba(0,0,0,0.06)' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
                <div style={{ height: 130, overflow: 'hidden', background: '#EDE6D4' }}>
                  <img src={item.image_url} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
                </div>
                <div style={{ padding: '0.7rem' }}>
                  <div style={{ fontSize: '0.65rem', color: '#E8622A', textTransform: 'uppercase', fontWeight: 500, marginBottom: 2 }}>{item.category}</div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 500, color: '#1A1208', marginBottom: 4, lineHeight: 1.3 }}>{item.title}</div>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.92rem', fontWeight: 800, color: '#8B6914' }}>{formatPrice(item.price)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mobile sticky bottom bar */}
      {isMobile && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#161616', borderTop: '0.5px solid rgba(255,255,255,0.1)', padding: '0.75rem 1rem', display: 'flex', gap: '0.75rem', zIndex: 100 }}>
          <button onClick={handleWhatsApp}
            style={{ flex: 1, padding: '0.75rem', background: '#25D366', color: '#fff', border: 'none', borderRadius: 12, fontSize: '0.88rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
            <WaIcon size={16} /> Chat
          </button>
          <button onClick={handleBuyNow}
            style={{ flex: 1, padding: '0.75rem', background: '#E8622A', color: '#fff', border: 'none', borderRadius: 12, fontSize: '0.88rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 700, cursor: 'pointer' }}>
            Buy Now
          </button>
          <button onClick={handleAddToCart}
            style={{ padding: '0.75rem', background: 'rgba(255,255,255,0.1)', border: '0.5px solid rgba(255,255,255,0.2)', color: '#F5F0E8', borderRadius: 12, cursor: 'pointer', position: 'relative' }}>
            <CartIcon size={20} />
            {cartCount > 0 && (
              <div style={{ position: 'absolute', top: 4, right: 4, background: '#E8622A', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: '0.6rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {cartCount}
              </div>
            )}
          </button>
        </div>
      )}

    </div>
  );
}
